import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { 
  ChevronRight, 
  Clock, 
  Calendar, 
  ChevronDown, 
  ChevronUp,
  CheckCircle2,
  Lightbulb,
  ArrowRight,
  FileText,
  Target,
  Layers,
  Construction,
  Wrench,
  Settings,
  Gauge,
  AlertTriangle,
  Cog,
  Activity,
  Ruler,
  ExternalLink,
  CircleDot,
  ShieldCheck,
  HelpCircle,
  BarChart3,
  TrendingUp,
  Eye,
  Search,
  ClipboardCheck,
  BadgeCheck,
  XCircle,
  DollarSign,
  Truck,
  Timer,
  ThumbsUp,
  ThumbsDown,
  Scale,
  FileCheck,
  AlertOctagon
} from 'lucide-react';

const ForeuseOccasionMicropieuxGuide = () => {
  const [activeSection, setActiveSection] = useState('');
  const [openFaq, setOpenFaq] = useState(null);

  // Table of contents
  const sections = [
    { id: 'section-1', title: "Définir votre besoin réel" },
    { id: 'section-2', title: "Les critères techniques essentiels" },
    { id: 'section-3', title: "Ce qu'il faut inspecter" },
    { id: 'section-4', title: "Les documents à exiger" },
    { id: 'section-5', title: "Les essais avant d'acheter" },
    { id: 'section-6', title: "Les pièges à éviter" },
    { id: 'section-7', title: "Neuf, occasion ou location ?" },
    { id: 'section-8', title: "Questions fréquentes" },
  ];

  const faqs = [
    {
      question: "Quel est le critère le plus important sur une foreuse à micropieux d'occasion ?",
      answer: "L'état de la tête de rotation et du circuit hydraulique, croisé avec les heures de forage réelles (pas seulement les heures moteur). C'est là que se concentrent les coûts de remise en état."
    },
    {
      question: "Combien d'heures est-ce « trop » ?",
      answer: "Il n'y a pas de seuil absolu. Une machine bien entretenue avec beaucoup d'heures et un carnet complet vaut souvent mieux qu'une machine peu utilisée mais négligée. L'entretien prime sur le compteur."
    },
    {
      question: "Faut-il refaire une VGP après l'achat ?",
      answer: "Oui, c'est vivement recommandé — voire nécessaire selon la date du dernier contrôle. La vérification générale périodique relève de la responsabilité de l'entreprise utilisatrice. Budgétez-la dès l'achat."
    },
    {
      question: "Quelles marques trouve-t-on sur le marché de l'occasion ?",
      answer: "Le marché européen compte des constructeurs bien établis (Comacchio, Casagrande, Soilmec, Klemm, Bauer, Sandvik, Atlas Copco/Epiroc…). Le critère décisif reste moins la marque que la disponibilité des pièces détachées et du service près de chez vous."
    },
    {
      question: "Où acheter une foreuse d'occasion ?",
      answer: "Auprès de revendeurs spécialisés en matériel de forage, sur les places de marché de matériel TP, ou en direct auprès d'entreprises qui renouvellent leur parc. Privilégiez les vendeurs qui acceptent une visite et un essai."
    }
  ];

  // Track scroll for active section
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY + 150;
      for (const section of sections) {
        const element = document.getElementById(section.id);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section.id);
            break;
          }
        }
      }
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      const offset = 100;
      const elementPosition = element.getBoundingClientRect().top + window.pageYOffset;
      window.scrollTo({ top: elementPosition - offset, behavior: 'smooth' });
    }
  };

  // Schema.org JSON-LD
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": "Comment choisir une foreuse d'occasion pour micropieux ? | Guide d'achat",
    "description": "Acheter une foreuse d'occasion pour micropieux : critères techniques, points d'usure, heures de forage, documents (CE, VGP) et pièges à éviter. Le guide d'achat complet.",
    "image": "https://geots.consulting/og-images/guide-foreuse-occasion.jpg",
    "author": {
      "@type": "Organization",
      "name": "GEOTS Consulting"
    },
    "publisher": {
      "@type": "Organization",
      "name": "GEOTS Consulting",
      "logo": {
        "@type": "ImageObject",
        "url": "https://geots.consulting/logo.png"
      }
    },
    "datePublished": "2026-07-19",
    "dateModified": "2026-07-19"
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(faq => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  // Technical criteria
  const technicalCriteria = [
    { criteria: "Couple de rotation", why: "La capacité à forer dans les terrains résistants. Critère n°1 de performance", icon: Cog },
    { criteria: "Poussée / traction", why: "Effort d'avancement et surtout de retrait du tubage", icon: TrendingUp },
    { criteria: "Hauteur hors tout", why: "Détermine l'accès aux sous-sols et sites exigus", icon: Ruler },
    { criteria: "Longueur de mât", why: "Conditionne la longueur des tiges et donc les cadences", icon: Layers },
    { criteria: "Puissance hydraulique", why: "Doit être cohérente avec les outils utilisés", icon: Gauge },
    { criteria: "Type de tête", why: "Rotation seule, rotopercussion, compatibilité tubage", icon: Settings },
    { criteria: "Poids et pression au sol", why: "Portance nécessaire, accès sur terrains meubles", icon: Scale },
    { criteria: "Compatibilité outillage", why: "Filetages, emmanchements : à vérifier impérativement", icon: Wrench },
  ];

  // Inspection points
  const inspectionPoints = [
    { name: "Tête de rotation", desc: "L'organe le plus sollicité et coûteux. Écoutez-la tourner, vérifiez les jeux, cherchez les fuites.", critical: true },
    { name: "Circuit hydraulique", desc: "Fuites, état des flexibles, propreté de l'huile, température en fonctionnement.", critical: true },
    { name: "Mât et chariot", desc: "Rectitude, usure des glissières, jeux excessifs.", critical: false },
    { name: "Chenilles et train de roulement", desc: "Usure, tension, état des galets.", critical: false },
    { name: "Structure", desc: "Réparations, soudures de fortune, redressements — signe d'incident non déclaré.", critical: true },
    { name: "Treuil et câbles", desc: "État général s'il y en a.", critical: false },
  ];

  // Required documents
  const requiredDocs = [
    { doc: "Marquage CE et déclaration de conformité", desc: "Les foreuses relèvent de la norme de sécurité NF EN 16228. Sans CE, exposition juridique.", icon: BadgeCheck },
    { doc: "Rapport VGP à jour", desc: "Vérifications générales périodiques annuelles. Un rapport récent sans réserve est un bon indicateur.", icon: ClipboardCheck },
    { doc: "Carnet d'entretien", desc: "L'historique de maintenance. Un carnet bien tenu vaut mieux qu'un compteur bas.", icon: FileText },
    { doc: "Manuel constructeur", desc: "Notice d'instructions indispensable.", icon: FileCheck },
    { doc: "Numéro de série", desc: "À recouper avec l'année déclarée.", icon: Search },
    { doc: "Disponibilité pièces détachées", desc: "Une machine orpheline peut devenir immobilisable.", icon: Wrench },
  ];

  // Traps to avoid
  const traps = [
    { trap: "Prix anormalement bas", desc: "Il y a toujours une raison. Cherchez-la avant d'acheter.", icon: DollarSign },
    { trap: "Machine repeinte de frais", desc: "La peinture neuve masque parfois des réparations ou de la corrosion.", icon: Eye },
    { trap: "Compteur d'heures suspect", desc: "Les compteurs peuvent être remis à zéro. Recoupez avec l'usure visible.", icon: Timer },
    { trap: "Absence de documents", desc: "Pas de CE, pas de VGP = coûts et risques que vous rachetez.", icon: XCircle },
    { trap: "Achat sur photos uniquement", desc: "Exigez au minimum une vidéo en fonctionnement, idéalement une visite.", icon: AlertOctagon },
    { trap: "Oublier les frais annexes", desc: "Transport, remise en conformité, VGP, révision, adaptation outillage.", icon: Truck },
  ];

  // Comparison table data
  const comparisonData = [
    { type: "Occasion", pertinent: "Activité régulière, budget contraint, compétence mécanique interne", avantages: "Investissement réduit, disponibilité immédiate", vigilance: "État réel, documents, pièces détachées" },
    { type: "Neuf", pertinent: "Usage intensif, besoin de garantie et dernières technologies", avantages: "Garantie, fiabilité, conformité assurée", vigilance: "Coût, délai de livraison" },
    { type: "Location", pertinent: "Chantier ponctuel, test d'un marché, pic d'activité", avantages: "Aucune immobilisation, matériel entretenu", vigilance: "Coût cumulé sur longue durée" },
  ];

  return (
    <>
      <Helmet>
        <title>Comment choisir une foreuse d'occasion pour micropieux ? | Guide d'achat</title>
        <meta name="description" content="Acheter une foreuse d'occasion pour micropieux : critères techniques, points d'usure, heures de forage, documents (CE, VGP) et pièges à éviter. Le guide d'achat complet." />
        <link rel="canonical" href="https://geots.consulting/guides/foreuse-occasion-micropieux" />
        <meta property="og:title" content="Comment choisir une foreuse d'occasion pour micropieux ? | Guide d'achat" />
        <meta property="og:description" content="Acheter une foreuse d'occasion pour micropieux : critères techniques, points d'usure, heures de forage, documents (CE, VGP) et pièges à éviter. Le guide d'achat complet." />
        <meta property="og:url" content="https://geots.consulting/guides/foreuse-occasion-micropieux" />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
      </Helmet>

      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        {/* Hero Section */}
        <div className="bg-gradient-to-br from-[#0A2540] via-[#0A2540] to-[#1a3a5c] text-white">
          <div className="container mx-auto px-4 py-12 md:py-20">
            {/* Breadcrumb */}
            <nav className="flex items-center gap-2 text-sm mb-8 text-gray-300">
              <Link to="/" className="hover:text-white">Accueil</Link>
              <ChevronRight className="w-4 h-4" />
              <Link to="/guides" className="hover:text-white">Guides</Link>
              <ChevronRight className="w-4 h-4" />
              <span className="text-white">Foreuse occasion micropieux</span>
            </nav>

            <div className="max-w-4xl">
              <div className="inline-flex items-center gap-2 bg-[#635BFF]/20 text-[#00D4FF] px-4 py-2 rounded-full text-sm font-medium mb-6">
                <Search className="w-4 h-4" />
                Guide d'achat
              </div>
              
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
                Comment choisir une foreuse d'occasion
                <span className="block text-transparent bg-clip-text bg-gradient-to-r from-[#00D4FF] to-[#635BFF] mt-2">
                  pour micropieux ?
                </span>
              </h1>

              <p className="text-xl text-gray-300 mb-8 leading-relaxed max-w-3xl">
                Acheter une foreuse d'occasion pour faire des micropieux, c'est souvent le bon calcul : une machine neuve 
                représente un investissement lourd, et le marché de l'occasion offre des opportunités réelles. Mais entre 
                une bonne affaire et un gouffre financier, la frontière est mince. Voici les critères qui comptent vraiment, 
                et les pièges à éviter avant de signer.
              </p>

              <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  <span>Mis à jour : Juillet 2026</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4" />
                  <span>Temps de lecture : 12 min</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="container mx-auto px-4 py-12">
          <div className="flex flex-col lg:flex-row gap-12">
            {/* Sidebar - Table of Contents */}
            <aside className="lg:w-72 flex-shrink-0">
              <div className="lg:sticky lg:top-24">
                <div className="bg-white rounded-xl shadow-lg border p-6">
                  <h3 className="font-bold text-[#0A2540] mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#635BFF]" />
                    Sommaire
                  </h3>
                  <nav className="space-y-1">
                    {sections.map((section, index) => (
                      <button
                        key={section.id}
                        onClick={() => scrollToSection(section.id)}
                        className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-all flex items-center gap-2 ${
                          activeSection === section.id
                            ? 'bg-[#635BFF]/10 text-[#635BFF] font-medium'
                            : 'text-gray-600 hover:bg-gray-100'
                        }`}
                      >
                        <span className="w-5 h-5 rounded-full bg-gray-100 flex items-center justify-center text-xs font-medium">
                          {index + 1}
                        </span>
                        <span className="line-clamp-2">{section.title}</span>
                      </button>
                    ))}
                  </nav>
                </div>

                {/* Related Guides */}
                <div className="bg-gradient-to-br from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 mt-6 border border-[#635BFF]/10">
                  <h4 className="font-bold text-[#0A2540] mb-4">Guides connexes</h4>
                  <div className="space-y-3">
                    <Link to="/guides/micropieux" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Micropieux : guide complet
                    </Link>
                    <Link to="/guides/materiel-de-forage" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Matériel de forage
                    </Link>
                    <Link to="/guides/comment-choisir-foreuse-geotechnique" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Comment choisir une foreuse ?
                    </Link>
                    <Link to="/guides/enregistreur-parametres-forage" className="flex items-center gap-2 text-sm text-gray-600 hover:text-[#635BFF] transition-colors">
                      <ArrowRight className="w-4 h-4" />
                      Enregistreur de paramètres
                    </Link>
                  </div>
                </div>
              </div>
            </aside>

            {/* Main Article */}
            <article className="flex-1 max-w-3xl">
              {/* Section 1: Define your need */}
              <section id="section-1" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-[#635BFF]/10 flex items-center justify-center">
                    <Target className="w-5 h-5 text-[#635BFF]" />
                  </span>
                  D'abord : définir votre besoin réel
                </h2>

                <div className="prose prose-lg max-w-none text-gray-700 mb-6">
                  <p>
                    Avant même de regarder les annonces, posez-vous les bonnes questions. Une foreuse à micropieux mal 
                    dimensionnée par rapport à vos chantiers, c'est un investissement perdu — même à bon prix.
                  </p>
                </div>

                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-5 border shadow-sm">
                    <h3 className="font-bold text-[#0A2540] mb-2 flex items-center gap-2">
                      <CircleDot className="w-5 h-5 text-[#635BFF]" />
                      Quels diamètres allez-vous forer ?
                    </h3>
                    <p className="text-gray-600">
                      Le micropieu se définit par un diamètre de forage <strong>inférieur à 300 mm</strong> (norme NF EN 14199). 
                      Mais entre 90 mm et 250 mm, les exigences en couple et en poussée ne sont pas les mêmes.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-5 border shadow-sm">
                    <h3 className="font-bold text-[#0A2540] mb-2 flex items-center gap-2">
                      <Ruler className="w-5 h-5 text-[#635BFF]" />
                      Quelles profondeurs ?
                    </h3>
                    <p className="text-gray-600">
                      En reprise en sous-œuvre de maison individuelle, on cherche souvent le bon sol entre 5 et 15 mètres. 
                      Sur des ouvrages plus lourds, on descend plus profond — avec des besoins de puissance et de longueur de mât différents.
                    </p>
                  </div>

                  <div className="bg-white rounded-xl p-5 border shadow-sm">
                    <h3 className="font-bold text-[#0A2540] mb-2 flex items-center gap-2">
                      <Layers className="w-5 h-5 text-[#635BFF]" />
                      Quels terrains ?
                    </h3>
                    <p className="text-gray-600">
                      Sols meubles, terrains hétérogènes avec blocs, substratum rocheux ? Le besoin en couple, en percussion 
                      et en type d'outillage en découle directement.
                    </p>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
                    <h3 className="font-bold text-amber-800 mb-2 flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5" />
                      Quelles conditions d'accès ?
                    </h3>
                    <p className="text-amber-700">
                      C'est LE critère souvent sous-estimé. Si vous visez la reprise en sous-œuvre en sous-sol ou en cave, 
                      la <strong>hauteur libre disponible</strong> et la <strong>largeur de passage</strong> deviennent des 
                      contraintes absolues. Une machine trop haute ou trop large, même excellente, sera inutilisable sur vos chantiers.
                    </p>
                  </div>
                </div>
              </section>

              {/* Section 2: Technical criteria */}
              <section id="section-2" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                    <Settings className="w-5 h-5 text-blue-600" />
                  </span>
                  Les critères techniques essentiels
                </h2>

                <div className="space-y-3 mb-6">
                  {technicalCriteria.map((item, index) => {
                    const IconComponent = item.icon;
                    return (
                      <div 
                        key={index}
                        className="bg-white rounded-xl p-4 border hover:border-[#635BFF]/50 hover:shadow-md transition-all"
                      >
                        <div className="flex items-start gap-4">
                          <div className="w-10 h-10 rounded-lg bg-[#635BFF]/10 flex items-center justify-center flex-shrink-0">
                            <IconComponent className="w-5 h-5 text-[#635BFF]" />
                          </div>
                          <div>
                            <h3 className="font-bold text-[#0A2540] mb-1">{item.criteria}</h3>
                            <p className="text-gray-600 text-sm">{item.why}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="bg-red-50 border border-red-200 rounded-xl p-6">
                  <div className="flex items-start gap-3">
                    <AlertTriangle className="w-6 h-6 text-red-600 flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-red-800 mb-2">Le piège de la compatibilité outillage</h4>
                      <p className="text-red-700">
                        Une machine parfaite mais dont les filetages et emmanchements ne correspondent pas à votre stock 
                        de tiges et d'outils, c'est un surcoût immédiat en rachat d'outillage. <strong>Vérifiez avant, pas après.</strong>
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 3: What to inspect */}
              <section id="section-3" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-green-100 flex items-center justify-center">
                    <Eye className="w-5 h-5 text-green-600" />
                  </span>
                  Ce qu'il faut inspecter sur une machine d'occasion
                </h2>

                {/* Hours explanation */}
                <div className="bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 mb-6 border-l-4 border-[#635BFF]">
                  <h3 className="font-bold text-[#0A2540] mb-3 flex items-center gap-2">
                    <Timer className="w-5 h-5 text-[#635BFF]" />
                    Les heures — mais lesquelles ?
                  </h3>
                  <p className="text-gray-700 mb-3">
                    C'est le point le plus mal compris. Une annonce affiche souvent les <strong>heures moteur</strong>, 
                    alors que ce qui use réellement une foreuse, ce sont les <strong>heures de forage</strong> (ou heures marteau). 
                    Une machine peut afficher 5 000 heures moteur pour seulement 1 500 heures de forage — ou l'inverse.
                  </p>
                  <p className="text-[#635BFF] font-medium">
                    👉 Demandez systématiquement les deux. Et croisez avec l'année de fabrication et le numéro de série.
                  </p>
                </div>

                {/* Inspection points */}
                <h3 className="font-bold text-[#0A2540] text-lg mb-4">Les points d'usure critiques</h3>
                <div className="space-y-3 mb-6">
                  {inspectionPoints.map((point, index) => (
                    <div 
                      key={index}
                      className={`rounded-xl p-4 border-2 ${
                        point.critical 
                          ? 'bg-red-50 border-red-200' 
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <div className="flex items-start gap-3">
                        {point.critical ? (
                          <AlertTriangle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                        ) : (
                          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        )}
                        <div>
                          <h4 className={`font-bold mb-1 ${point.critical ? 'text-red-800' : 'text-[#0A2540]'}`}>
                            {point.name}
                            {point.critical && <span className="ml-2 text-xs bg-red-100 text-red-600 px-2 py-0.5 rounded">CRITIQUE</span>}
                          </h4>
                          <p className={point.critical ? 'text-red-700 text-sm' : 'text-gray-600 text-sm'}>{point.desc}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Additional equipment */}
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-6">
                  <h4 className="font-bold text-blue-800 mb-3">Les organes annexes</h4>
                  <p className="text-blue-700">
                    Une foreuse à micropieux ne travaille jamais seule. Vérifiez ce qui est inclus : <strong>centrale à coulis</strong> 
                    (malaxeur, agitateur), <strong>pompe d'injection</strong>, <strong>compresseur</strong> si rotopercussion, 
                    et le <strong>jeu de tiges et outils</strong>. Un lot complet et cohérent vaut souvent bien plus que la machine seule.
                  </p>
                </div>
              </section>

              {/* Section 4: Required documents */}
              <section id="section-4" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-purple-100 flex items-center justify-center">
                    <FileCheck className="w-5 h-5 text-purple-600" />
                  </span>
                  Les documents à exiger absolument
                </h2>

                <p className="text-gray-700 mb-6">
                  C'est ce qui sépare un achat sérieux d'une prise de risque :
                </p>

                <div className="space-y-3">
                  {requiredDocs.map((item, index) => {
                    const IconComponent = item.icon;
                    return (
                      <div 
                        key={index}
                        className="bg-white rounded-xl p-4 border flex items-start gap-4"
                      >
                        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-[#0A2540] mb-1">{item.doc}</h3>
                          <p className="text-gray-600 text-sm">{item.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Section 5: Tests before buying */}
              <section id="section-5" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-cyan-100 flex items-center justify-center">
                    <Activity className="w-5 h-5 text-cyan-600" />
                  </span>
                  Les essais à réaliser avant d'acheter
                </h2>

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
                  <p className="text-amber-800 font-medium">
                    ⚠️ Ne signez jamais sans avoir vu la machine <strong>tourner</strong>, idéalement <strong>en forage réel</strong> 
                    et pas juste au ralenti sur un parking.
                  </p>
                </div>

                <div className="space-y-4 mb-6">
                  <div className="flex items-start gap-4 bg-white rounded-xl p-5 border">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#635BFF] to-[#00D4FF] flex items-center justify-center text-white font-bold flex-shrink-0">
                      1
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0A2540] mb-1">Démarrage à froid</h3>
                      <p className="text-gray-600 text-sm">Fumées, bruits, temps de mise en pression.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 bg-white rounded-xl p-5 border">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#635BFF] to-[#00D4FF] flex items-center justify-center text-white font-bold flex-shrink-0">
                      2
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0A2540] mb-1">Fonctionnement de tous les mouvements</h3>
                      <p className="text-gray-600 text-sm">Rotation, poussée, retrait, relevage du mât, translation, calage.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 bg-white rounded-xl p-5 border">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#635BFF] to-[#00D4FF] flex items-center justify-center text-white font-bold flex-shrink-0">
                      3
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0A2540] mb-1">Montée en température</h3>
                      <p className="text-gray-600 text-sm">C'est là qu'apparaissent les faiblesses hydrauliques.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 bg-white rounded-xl p-5 border">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#635BFF] to-[#00D4FF] flex items-center justify-center text-white font-bold flex-shrink-0">
                      4
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0A2540] mb-1">Un forage d'essai</h3>
                      <p className="text-gray-600 text-sm">Si c'est possible : rien ne remplace ça.</p>
                    </div>
                  </div>

                  <div className="flex items-start gap-4 bg-white rounded-xl p-5 border">
                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#635BFF] to-[#00D4FF] flex items-center justify-center text-white font-bold flex-shrink-0">
                      5
                    </div>
                    <div>
                      <h3 className="font-bold text-[#0A2540] mb-1">Vérification des sécurités</h3>
                      <p className="text-gray-600 text-sm">Arrêts d'urgence, protections, signalisation.</p>
                    </div>
                  </div>
                </div>

                <div className="bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 border-l-4 border-[#00D4FF]">
                  <div className="flex items-start gap-3">
                    <Lightbulb className="w-6 h-6 text-[#00D4FF] flex-shrink-0 mt-1" />
                    <div>
                      <h4 className="font-bold text-[#0A2540] mb-2">Le meilleur investissement</h4>
                      <p className="text-gray-700">
                        Faire accompagner la visite par un <strong>mécanicien ou un expert du forage</strong>. 
                        Le coût de son intervention est dérisoire face à une tête de rotation à remplacer.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section 6: Traps to avoid */}
              <section id="section-6" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-red-100 flex items-center justify-center">
                    <AlertOctagon className="w-5 h-5 text-red-600" />
                  </span>
                  Les pièges classiques à éviter
                </h2>

                <div className="grid gap-4">
                  {traps.map((item, index) => {
                    const IconComponent = item.icon;
                    return (
                      <div 
                        key={index}
                        className="bg-red-50 border border-red-200 rounded-xl p-4 flex items-start gap-4"
                      >
                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-red-800 mb-1">🚩 {item.trap}</h3>
                          <p className="text-red-700 text-sm">{item.desc}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* Section 7: Comparison */}
              <section id="section-7" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center">
                    <Scale className="w-5 h-5 text-orange-600" />
                  </span>
                  Neuf, occasion ou location : le bon arbitrage
                </h2>

                <div className="overflow-x-auto mb-6">
                  <div className="space-y-4">
                    {comparisonData.map((row, index) => (
                      <div 
                        key={index}
                        className={`rounded-xl p-5 border-2 ${
                          row.type === 'Occasion' 
                            ? 'bg-blue-50 border-blue-200' 
                            : row.type === 'Neuf' 
                            ? 'bg-green-50 border-green-200'
                            : 'bg-purple-50 border-purple-200'
                        }`}
                      >
                        <h3 className={`font-bold text-lg mb-3 ${
                          row.type === 'Occasion' 
                            ? 'text-blue-800' 
                            : row.type === 'Neuf' 
                            ? 'text-green-800'
                            : 'text-purple-800'
                        }`}>{row.type}</h3>
                        <div className="grid md:grid-cols-3 gap-4 text-sm">
                          <div>
                            <p className="font-medium text-gray-700 mb-1">Pertinent si :</p>
                            <p className="text-gray-600">{row.pertinent}</p>
                          </div>
                          <div>
                            <p className="font-medium text-gray-700 mb-1 flex items-center gap-1">
                              <ThumbsUp className="w-4 h-4 text-green-500" /> Avantages :
                            </p>
                            <p className="text-gray-600">{row.avantages}</p>
                          </div>
                          <div>
                            <p className="font-medium text-gray-700 mb-1 flex items-center gap-1">
                              <ThumbsDown className="w-4 h-4 text-amber-500" /> Vigilance :
                            </p>
                            <p className="text-gray-600">{row.vigilance}</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-gradient-to-r from-[#635BFF]/5 to-[#00D4FF]/5 rounded-xl p-6 border-l-4 border-[#635BFF]">
                  <p className="text-gray-700">
                    Si vous démarrez une activité micropieux, la <strong>location</strong> est souvent le bon moyen de 
                    valider votre marché avant d'immobiliser du capital dans une machine.
                  </p>
                </div>
              </section>

              {/* Section 8: FAQ */}
              <section id="section-8" className="mb-16">
                <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-6 flex items-center gap-3">
                  <span className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                    <HelpCircle className="w-5 h-5 text-indigo-600" />
                  </span>
                  Questions fréquentes
                </h2>

                <div className="space-y-4">
                  {faqs.map((faq, index) => (
                    <div 
                      key={index}
                      className="bg-white rounded-xl border overflow-hidden"
                    >
                      <button
                        onClick={() => setOpenFaq(openFaq === index ? null : index)}
                        className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50 transition-colors"
                      >
                        <span className="font-semibold text-[#0A2540] pr-4">{faq.question}</span>
                        {openFaq === index ? (
                          <ChevronUp className="w-5 h-5 text-[#635BFF] flex-shrink-0" />
                        ) : (
                          <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                        )}
                      </button>
                      {openFaq === index && (
                        <div className="px-5 pb-5 text-gray-600 border-t">
                          <p className="pt-4">{faq.answer}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>

              {/* Beyond the machine */}
              <div className="bg-gray-50 rounded-xl p-6 border mb-8">
                <h3 className="font-bold text-[#0A2540] text-lg mb-4">Bien s'équiper au-delà de la machine</h3>
                <p className="text-gray-700 mb-4">
                  Une foreuse à micropieux n'est qu'un maillon. Il faut aussi l'<strong>outillage compatible</strong> 
                  (tiges, tubages, outils adaptés au terrain), la <strong>centrale à coulis et la pompe d'injection</strong>, 
                  et l'<strong>instrumentation</strong> pour tracer l'exécution.
                </p>
                <Link 
                  to="/guides/enregistreur-parametres-forage"
                  className="inline-flex items-center gap-2 text-[#635BFF] font-medium hover:underline"
                >
                  Guide enregistreur de paramètres <ArrowRight className="w-4 h-4" />
                </Link>
              </div>

              {/* CTA Box */}
              <div className="bg-gradient-to-br from-[#0A2540] to-[#1a3a5c] rounded-2xl p-8 text-white mb-8">
                <h3 className="text-xl font-bold mb-4">
                  Vous cherchez une foreuse à micropieux ?
                </h3>
                <p className="text-gray-300 mb-6">
                  GEOTS met en relation les acteurs de la géotechnique et des fondations spéciales : vendeurs et loueurs 
                  de matériel de forage, spécialistes de l'instrumentation et de l'étalonnage, entreprises et bureaux d'études.
                </p>
                <div className="flex flex-wrap gap-4">
                  <Link 
                    to="/calcul/estimer-ma-foreuse"
                    className="inline-flex items-center gap-2 bg-[#635BFF] hover:bg-[#5248d9] text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    <Gauge className="w-4 h-4" />
                    Estimer la valeur de ma foreuse
                  </Link>
                  <Link 
                    to="/annuaire/materiel-equipements-forage/foreuses-occasion-micropieux"
                    className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                  >
                    Voir l'annuaire foreuses
                    <ExternalLink className="w-4 h-4" />
                  </Link>
                </div>
              </div>

              {/* Disclaimer */}
              <div className="text-sm text-gray-500 italic border-t pt-8">
                Ce guide est fourni à titre informatif. L'achat d'un engin de chantier d'occasion doit s'accompagner 
                d'une vérification technique par un professionnel qualifié et du contrôle de la conformité réglementaire de la machine.
              </div>
            </article>
          </div>
        </div>

        {/* Related Guides Section */}
        <div className="bg-gray-50 border-t py-16">
          <div className="container mx-auto px-4">
            <h2 className="text-2xl md:text-3xl font-bold text-[#0A2540] mb-8 text-center">
              Continuez votre lecture
            </h2>
            <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <Link 
                to="/guides/micropieux"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-cyan-100 flex items-center justify-center mb-4">
                  <CircleDot className="w-6 h-6 text-cyan-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Micropieux : guide complet
                </h3>
                <p className="text-gray-600 text-sm">
                  Types I à IV, mise en œuvre et reprise en sous-œuvre.
                </p>
              </Link>

              <Link 
                to="/guides/materiel-de-forage"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mb-4">
                  <Wrench className="w-6 h-6 text-orange-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Matériel de forage : guide complet
                </h3>
                <p className="text-gray-600 text-sm">
                  Foreuses, outils et instrumentation.
                </p>
              </Link>

              <Link 
                to="/guides/comment-choisir-foreuse-geotechnique"
                className="bg-white rounded-xl p-6 border hover:shadow-lg transition-shadow group"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center mb-4">
                  <Construction className="w-6 h-6 text-blue-600" />
                </div>
                <h3 className="font-bold text-[#0A2540] mb-2 group-hover:text-[#635BFF] transition-colors">
                  Comment choisir une foreuse ?
                </h3>
                <p className="text-gray-600 text-sm">
                  10 critères, 4 familles, comparatif complet.
                </p>
              </Link>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ForeuseOccasionMicropieuxGuide;
